import { ModalService } from "@app/services/modal.service"
import { ApiService, pluck } from "@app/services/api.service"
import { LangService } from "@app/services/lang.service"
import {
	booleanAttribute,
	Component,
	Input,
	OnInit,
	SimpleChange,
	ViewChild,
	ChangeDetectionStrategy,
} from "@angular/core"
import { SearchSortService } from "@app/services/search-sort.service"
import { StoreService } from "@app/services/store.service"
import { EventsService } from "@app/services/events.service"
import { PermsService } from "@app/services/perms.service"
import { DatePickerComponent } from "@app/utils/date-picker/date-picker.component"
import { dateDisplay } from "@app/helpers"
import { COMMON_IMPORTS } from "../common-imports"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import {
	BtnExtComponent,
	SortThComponent,
	TestDisplayComponent,
} from "@app/utils/utils.component"
import { DateFormatPipe } from "@app/utils/date-format.pipe"
import { FinNumberPipe } from "@app/utils/finNumber.pipe"
import { SafeHtmlPipe } from "@app/utils/safe-html.pipe"
import { ServerImgPipe } from "@app/utils/server-img.pipe"
import { ImageGalleryComponent } from "@app/image-gallery/image-gallery.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { PatientFusesComponent } from "@app/patient-fuses/patient-fuses.component"
import { AddDocumentComponent } from "@app/add-document/add-document.component"
import { PatientComponent } from "@app/patient/patient.component"

const searchMinChars = 3

@Component({
	selector: "app-mixed-list",
	imports: [
		COMMON_IMPORTS,
		LangValPipe,
		GenInputComponent,
		GenButtonGroupComponent,
		DatePickerComponent,
		SortThComponent,
		DateFormatPipe,
		FinNumberPipe,
		SafeHtmlPipe,
		TestDisplayComponent,
		ServerImgPipe,
		BtnExtComponent,
		ImageGalleryComponent,
		ModalWrapComponent,
		PatientFusesComponent,
		AddDocumentComponent,
		PatientComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./mixed-list.component.html",
})
export class MixedListComponent {
	modalProps: any = null
	@Input() patient: any = null
	@Input() followup_id: any = null
	@Input({ transform: booleanAttribute }) preventShowingInList: boolean = false
	mixedListChosenOpt: string = "without"
	patientImages: any[] = []
	withDescription: string = "yes"
	withPersonalMemorandum: string = "no"
	withSupervisorNotes: string = "no"
	showFutureMeetings: string = "no"
	searchStr: string = ""
	objectCollection: any[] = []
	objectCollectionShown: any[] = []
	searchableFields: string[] = [
		"date_formatted",
		"description_normalized",
		"html_text_normalized",
		"meetingtype_name",
		"diagOrMedic",
	]

	sortField: string = "date"
	sortIsAsc: boolean = false
	dateFields: string[] = ["date"]

	period: string = ""

	date_from: string = ""
	date_to: string = ""
	@ViewChild("fromDate") fromDate: DatePickerComponent
	@ViewChild("toDate") toDate: DatePickerComponent
	periodInputs: any[] = [
		"last_90_days",
		"last_365_days",
		"all_options",
		"date_range",
	].map((it) => ({ name: it, value: it })) //'last_30_days',
	currentPeriod: string = "last_90_days" //FinResourcePeriods
	isOpeningDocument: boolean = false

	constructor(
		protected searchSortService: SearchSortService,
		public apiService: ApiService,
		public modalService: ModalService,
		public store: StoreService,
		public eventsService: EventsService,
		public permsService: PermsService,
		public lang: LangService
	) {}

	ngOnInit() {
		this.load()
	}
	ngOnChanges(changes: SimpleChange) {
		//follow changes to input values. if any changes - run initRun
		if (changes["patient"] || changes["followup_id"]) {
			this.load()
		}
	}
	async load() {
		this.objectCollection = []
		this.objectCollectionShown = []
		this.withDescription = "yes"
		const cc = this.store.cc()
		this.showFutureMeetings = cc.show_future_meetings
		this.currentPeriod = cc.mixed_list_period || "last_365_days"
		this.withPersonalMemorandum = cc.mixed_list_pm || "no"
		this.withSupervisorNotes = cc.mixed_list_sn || "no"
		this.sortIsAsc = cc.mixed_list_sort_desc === "no"
		await this.loadObjectCollections()
		this.chPeriod()
		// this.search();
	}
	getDatetime(row: any) {
		if (row.type === "medicpatient") {
			return new Date(`${row.date} 00:00:00`)
		}
		return new Date(`${row.date} ${row.time || `00:00:00`}`)
	}
	async loadObjectCollections() {
		this.patientImages = await this.apiService.post("get_fp_images", {
			patient_id: this.patient.id,
		})
		this.objectCollection = this.patient.mixed_list
		this.objectCollection.forEach((row) => {
			row[this.store.getDateExpandName("date")] = new Date(row.date)

			row.datetime = this.getDatetime(row)
			row["date_formatted"] = dateDisplay(row.date)
			switch (row.type) {
				case "followup":
					row.images = this.patientImages.filter(
						(it) => it.followup_id == row.id
					)
					break
				case "testfill":
					row.test = this.patient.tests.find((it) => it.testfill_id == row.id)
					row.html_text = row.test?.html_text
					break
			}
			row.description_normalized = this.store.normalizeHtml(
				row.description || ""
			)
			row.html_text_normalized = this.store.normalizeHtml(row.html_text || "")
		})

		this.search()
	}
	chPeriod() {
		if (this.currentPeriod == "date_range") {
			this.search()
			return
		}
		//plant new dates
		//let from = null;
		const [from, to] = this.store.getPriodFromTo(this.currentPeriod)
		this.date_from = from
		this.date_to = to
		if (this.date_from && this.fromDate) {
			this.fromDate.refreshWithModel()
			this.toDate.refreshWithModel()
		}
		this.search()

		const cc = this.store.cc()
		cc.mixed_list_period = this.currentPeriod
		this.apiService.post("update_configcliniq_data", cc)
		sessionStorage.setItem("cliniqData", JSON.stringify(cc))
	}
	saveCc() {
		const cc = this.store.cc()
		cc.mixed_list_pm = this.withPersonalMemorandum
		cc.mixed_list_sn = this.withSupervisorNotes
		this.apiService.post("update_configcliniq_data", cc)
		sessionStorage.setItem("cliniqData", JSON.stringify(cc))
	}
	limitById() {
		if (this.preventShowingInList) {
			this.objectCollectionShown = this.objectCollectionShown.filter(
				(it) => it.id != this.followup_id
			)
		}
	}
	search() {
		this.objectCollectionShown = this.objectCollection
		if (this.searchStr.length >= searchMinChars) {
			this.searchSortService.search(
				this,
				this.searchStr,
				"objectCollectionShown",
				"objectCollection"
			)
		}
		if (!this.searchStr) {
			if (this.currentPeriod != "all_options") {
				if (this.date_from) {
					const dateFrom = new Date(this.date_from)
					this.objectCollectionShown = this.objectCollectionShown.filter(
						(row) => {
							return dateFrom <= row[this.store.getDateExpandName("date")]
						}
					)
				}
				if (this.date_to) {
					const dateTo = new Date(this.date_to)
					this.objectCollectionShown = this.objectCollectionShown.filter(
						(row) => {
							return dateTo >= row[this.store.getDateExpandName("date")]
						}
					)
				}
			}
		}
		this.sortByField(this.sortField, true)
		this.limitById()
	}
	sortByDate(repeatSort: boolean = false) {
		if (!repeatSort) {
			this.searchSortService.updateSortField(this, "date")
		}
		let reverseFactor = this.sortIsAsc ? 1 : -1
		this.objectCollectionShown.sort((a, b) => {
			return (a.datetime.getTime() - b.datetime.getTime()) * reverseFactor
		})
	}
	sortByField(fieldName: string, repeatSort: boolean = false) {
		if (fieldName === "date") {
			this.sortByDate(repeatSort)
		} else {
			this.searchSortService.sort(
				this,
				"objectCollectionShown",
				fieldName,
				repeatSort
			)
		}
	}

	sort(fieldName: string, repeatSort: boolean = false) {
		this.sortByField(fieldName, repeatSort)
		this.limitById()
	}

	openGallery(imgObj: any) {
		const ind = this.patientImages.findIndex((it) => it.id == imgObj.id)
		this.modalProps = {
			name: "image-gallery",
			images: this.patientImages,
			curImagePosition: ind,
		}
	}

	async openRow(row: any) {
		if (
			!this.permsService?.perms?.clinical ||
			row?.type != "followup" ||
			row?.status == "deleted" ||
			row.id == this.followup_id
		) {
			return
		}
		// let followup=this.store.data.followups.find(it=>it.id==row.id);
		row.isFetching = true
		const res = await this.apiService.post("get_followup_by_id", row, "id")
		row.isFetching = false
		if (!res?.followup) {
			return
		}
		const followup = this.store.removeDecItem(res.followup)
		// if(!followup){
		// }
		this.modalProps = { name: "patient", followup }
	}

	async xlsxDownload() {
		this.store.downloadToastFunc(async () => {
			const sendObj: any = { id: this.patient.id }
			if (this.currentPeriod !== "all_options") {
				sendObj.date_from = this.date_from
				sendObj.date_to = this.date_to
			}
			const res = await this.apiService.download("print_mixed_list", sendObj)
			if (!res?.size) {
				return this.store.downloadFailedModal()
			}
			this.apiService.downloadFile(res, `mixed_list.xlsx`)
		})
	}

	async print() {
		let html = document.getElementById("section-to-print-mixed-list")?.innerHTML
		html = this.store.processHtmlPreDl(html || "")
		html = html.replace(/<thead\s*>/g, "<thead>")

		this.store.printWindow(
			`
		<html>
		<style type='text/css'>${this.store.getPrintStyle3()}</style>
		<body onload="window.print();window.onafterprint=()=>window.close();" style="direction:rtl">
		${html}
		</body>
		</html>`,
			true
		)
	}
	async addDocument() {
		this.isOpeningDocument = true
		setTimeout(async () => {
			const patient_id = this.patient.id
			const patient = await this.store.getPatient(patient_id)
			let html =
				'<div class="mixed-list-body for-print">' +
				document.getElementById("section-to-print-mixed-list")?.innerHTML +
				"</div>"

			//html = html.replace(/h2/g, "h6")

			//   html = html.replace(/h2/g, "h6")

			html = this.store.processHtmlPreDl(html)
			html = html.replace(/<thead\s*>/g, "<thead>")

			this.isOpeningDocument = false
			this.modalProps = {
				name: "add-document",
				patient_id: patient?.id,
				title: `${patient?.name} ${
					patient?.id_number
						? `${this.lang.getVal("id_number")} ${patient.id_number}`
						: ""
				}`,
				preText: html,
			}
		})
	}
	highlightSearch(t: string) {
		if (!this.searchStr || this.searchStr.length < searchMinChars) {
			return t
		}
		return t.replaceAll(
			this.searchStr,
			`<span class='autocomplete-highlight'>${this.searchStr}</span>`
		)
	}
}
